import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, map, of, switchMap, tap, throwError } from 'rxjs';
import { DialogService } from '../../components/dialog/dialog.service';
import { InteractionService } from '../../services/interaction.service';

@Component({
  selector: 'app-interaction-creator',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionCreatorComponent implements OnInit {
  private readonly _dialog = inject(DialogService);
  private readonly _interactions = inject(InteractionService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _location = inject(Location);

  ngOnInit(): void {
    const isTemplate = this._route.snapshot.url.some(
      (segment) => segment.path === 'templates'
    );

    const clientId = isTemplate
      ? null
      : this._route.parent?.snapshot.paramMap.get('clientId') ?? null;

    const singular = isTemplate ? 'template' : 'interactie';
    const routeBase = isTemplate ? '/templates' : '/interactions';

    this._dialog.prompt(
      {
        title: `Nieuwe ${singular}`,
        message: '',
        confirmText: 'Aanmaken',
        cancelText: 'Annuleren',
        onConfirm: (result) =>
          of(result).pipe(
            filter((value) => value != null),
            map((label) => ({ label, clientId })),
            switchMap((data) =>
              this._interactions.create(data).pipe(
                tap((newId) => {
                  this._router.navigate([routeBase, newId], {
                    replaceUrl: true,
                  });
                }),
                catchError((error) => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message: `Er is iets misgegaan bij het aanmaken van een ${singular}. Probeer het later nog eens`,
                    confirmText: 'OK',
                  });
                  return throwError(() => error);
                })
              )
            )
          ),
        input: {
          mode: 'input',
          placeholder: `Naam ${singular}`,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een naam in',
            },
            pattern: {
              validator: Validators.pattern(/^[^\\/:*?"<>|]*$/),
              errorMessage:
                'De volgende tekens zijn niet toegestaan: \\ / : * ? " < > |',
            },
            maxlength: {
              validator: Validators.maxLength(100),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;
                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
        disableClose: true,
      }
    )
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          this._location.back();
        }
      });
  }
}
